import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { golfApi, type GolfHoleSummary } from "./api";
import { GolfBoardDialog } from "./GolfBoard";
import { GolfPlay } from "./GolfPlay";
import { useMe } from "./me";
import "./Golf.css";

// Prompt Golf on Home (Review 19, D53): the five holes with par and your best score, Play and Board.
// Students can play from Meeting 2, "Say what you mean" (Mon Oct 19); teachers any time. Playing a
// hole is the #golf-<hole id> page, shown over Home (GolfPlay.tsx).

function readGolfHash(): string | null {
  return /^#golf-(hole-\d+)$/.exec(window.location.hash)?.[1] ?? null;
}

const dayLabel = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

export function PromptGolfEntry() {
  const me = useMe();
  const teacher = me?.user.role === "teacher";
  const [data, setData] = useState<{ open: boolean; opensOn: string; holes: GolfHoleSummary[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(readGolfHash());
  const [board, setBoard] = useState<GolfHoleSummary | null>(null);

  const load = () => golfApi.holes().then(setData, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    const onHash = () => setPlaying(readGolfHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  // Home doesn't scroll behind the play page; best scores refresh when it closes.
  useEffect(() => {
    document.documentElement.classList.toggle("golf-open", !!playing);
    if (!playing) void load();
    return () => document.documentElement.classList.remove("golf-open");
  }, [playing]);

  const play = (id: string) => {
    window.location.hash = `golf-${id}`;
  };
  const locked = !!data && !data.open;

  return (
    <section className="golf-home" aria-label="Prompt Golf">
      <div className="golf-home-head">
        <span className="golf-home-flag" aria-hidden="true">
          ⛳
        </span>
        <div>
          <h2>Prompt Golf</h2>
          <p className="small">
            Rebuild a mini-app by telling the helper what to build. Fewest messages wins, then fewest words. Say exactly what you mean the first time!
          </p>
        </div>
        {locked && (
          <span className="golf-lock" role="status">
            <span aria-hidden="true">🔒</span> Opens {dayLabel(data!.opensOn)}
          </span>
        )}
      </div>
      {error && <p className="page-error small">{error}</p>}
      {data && (
        <ol className="golf-holes">
          {data.holes.map((h) => (
            <li key={h.id} className={`golf-hole${locked ? " locked" : ""}`}>
              <span className="golf-hole-emoji" aria-hidden="true">
                {h.emoji}
              </span>
              <span className="golf-hole-name">
                <span className="eyebrow">Hole {h.number}</span>
                <b>{h.title}</b>
              </span>
              <span className="golf-hole-par">Par {h.par}</span>
              <span className="golf-hole-best">
                {h.best ? (
                  <>
                    <span className={`golf-tag k-${h.best.score.kind}`}>{h.best.score.label}</span>
                    <span className="small muted">
                      {h.best.strokes} {h.best.strokes === 1 ? "stroke" : "strokes"} · {h.best.words} words
                    </span>
                  </>
                ) : (
                  <span className="small muted">{locked ? "" : "Not played yet"}</span>
                )}
              </span>
              <span className="golf-hole-actions">
                {!locked && (
                  <button type="button" className="linklike small" onClick={() => setBoard(h)} aria-label={`Board for hole ${h.number}`}>
                    Board
                  </button>
                )}
                <button type="button" className={locked ? "outline small" : "primary small"} disabled={locked} onClick={() => play(h.id)} aria-label={`Play hole ${h.number}, ${h.title}`}>
                  {locked ? "Locked" : "Play"}
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {teacher && <p className="small muted golf-teacher-note">Students can play from Meeting 2 ({data ? dayLabel(data.opensOn) : "Mon Oct 19"}). Each message costs a helper coin, as usual.</p>}
      {board && <GolfBoardDialog holeId={board.id} title={`Hole ${board.number} · ${board.title}`} par={board.par} teacher={teacher} onClose={() => setBoard(null)} />}
      {playing && data?.open && createPortal(<GolfPlay key={playing} holeId={playing} onExit={() => (window.location.hash = "")} />, document.body)}
    </section>
  );
}
